import React, { FC, useEffect, useMemo, useRef, useState } from "react";
import {
  Button,
  Row,
  Col,
  Card,
  Select,
  DatePicker,
  Table,
} from "antd";
import {
  DownOutlined,
  FileSearchOutlined,
  EyeOutlined,
  FilterOutlined,
  RedoOutlined,
} from "@ant-design/icons";
import _ from "lodash";
import { ReactSVG } from "react-svg";
import { wrapRequestPayload } from "../../../helpers/request/RequestPayload";
import {
  getAggregateBdsPublishTopic,
  getAggregateBdsSubscriptionTopic,
  getAdminRolesTopic,
  getProjectsSubscriptionTopic,
  getProjectsRequestPublishTopic,
  getUsersSubscriptionTopic,
  getMemberPageUpdatedPublishTopic,
  getFocusPagePublishTopic,
} from "hooks/MQTTConstants";
import { useLocation } from "react-router-dom";
import { SocketTopics, UserTopics } from "constants/enums";
import { useSelector } from "react-redux";
import { RootState } from "reducers/rootReducer";
import { useDispatch } from "react-redux";
import VerticalGraph from "common/Graphs/VerticalGraph";
import MapPreview from "./MapPreview";
import TableFilterModal from "./TableFilterModal";
import useSocketActions from "../../../hooks/socketActions";
import { generateUUID } from "utils/appUtil";
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from "chart.js";
import { Pie } from "react-chartjs-2";
import { formatForRedis, attributeKeyGenerator } from "utils/appUtil";
import { GET_AGGREGATE } from "../../../reducers/reducer";
import "./Dashboard.scss";
ChartJS.register(ArcElement, Tooltip, Legend);
const { Option } = Select;

const DashboardModal: FC<{ socket: ISocket; params?: any }> = ({
  socket,
}) => {
  const { search } = useLocation();
  const { userRequests, projects } = useSelector(
    (state: RootState) => state.auth
  );
  const dispatch = useDispatch();
  const [projectFilter, setProjectFilter] = useState<string>("project");
  const [timeFilter, setTimeFilter] = useState<string>("state");
  const [projectFilterLoading, setProjectFilterLoading] =
    useState<boolean>(false);
  const [projectFilterValue, setProjectFilterValue] = useState<any>(undefined);
  const [timeFilterValue, setTimeFilterValue] = useState<any>(undefined);
  const [statusFilterLoading, setStatusFilterLoading] =
    useState<boolean>(false);
  const [timeFilterLoading, setTimeFilterLoading] = useState<boolean>(false);
  const [projectFilterOptions, setProjectFilterOptions] = useState<any[]>([]);
  const [timeFilterOptions, setTimeFilterOptions] = useState<any[]>([]);
  const [pages, setPages] = useState<any>();
  const [pageLocations, setPageLocations] = useState<any>([]);
  const filterUserRequestId = useRef(generateUUID());
  const startDate = useRef<any>(undefined);
  const endDate = useRef<any>(undefined);
  const pageOffset = useRef<any>(0);
  const selectedColor = useRef<any>(undefined);
  const statusTypes = [
    { label: "Created", name: "purple", color: "#463BBD" },
    { label: "Started", name: "blue", color: "#508AFA" },
    { label: "Assigned", name: "amber", color: "#FBAE16" },
    { label: "Escalated", name: "red", color: "#FF5F25" },
    { label: "Completed", name: "green", color: "#40CC52" },
    { label: "Processed", name: "grey", color: "#91A5C6" },
    { label: "Cancelled", name: "black", color: "#00000" },
  ];
  const [pageUpdated, setPageUpdated] = useState<any>();
  const [statusCount, setStatusCount] = useState<any>({
    all: 0,
    live: 0,
    ...statusTypes.reduce((a, v) => ({ ...a, [v.name]: 0 }), {}),
  });
  const [redrawCount, setRedrawCount] = useState<number>(0);
  const [chartData, setChartData] = useState<any>({
    labels: statusTypes.map((x) => {
      return x.label;
    }),
    datasets: [
      {
        label: "Page status",
        data: [1, 0, 0, 0, 0, 0, 0],
        backgroundColor: statusTypes.map((x) => {
          return x.color;
        }),
        borderWidth: 1,
      },
    ],
  });
  const [barChartData, setBarChartData] = useState<any>({
    labels: ["Status"],
    datasets: statusTypes.map((x) => {
      return {
        label: x.label,
        data: [0],
        backgroundColor: x.color,
      };
    }),
  });
  const [tableDataSource, setTableDataSource] = useState<any>([]);
  const [tableColumns, setTableColumns] = useState<any>([]);
  const [showTableFilterModal, setShowTableFilterModal] =
    useState<boolean>(false);
  const [tableHideList, setTableHideList] = useState<any>([]);
  const { getUserRequestPayload } = useSocketActions();
  const adminRoles = useRef(undefined);
  const pagePaths = useRef({
    project: undefined,
    user: undefined,
    status: undefined,
    area: undefined,
    time: undefined,
  });
  const loadPaths = useRef<any>([]);

  const setProjectPagePaths = (value) => {
    setProjectFilterValue(value);
    switch (projectFilter) {
      case "project":
        pagePaths.current.project = value
          ? `exists(@iotAssetConfigId) && @iotAssetConfigId === '${value}'`
          : undefined;
        pagePaths.current.user = undefined;
        setProjectFilterLoading(true);
        break;
      case "manager":
      case "responder":
      case "dispatcher":
        loadPaths.current = [
          {
            key: "user",
            value: `$.persistent.static.metadata.members.users.${value}`,
          },
        ];
        pagePaths.current.user = value ? `exists(@user)` : undefined;
        pagePaths.current.project = undefined;
        setProjectFilterLoading(true);
        break;
    }
    searchPage();
  };

  const setStatusPagePaths = (value) => {
    pagePaths.current.status = value
      ? `exists(@status) && @status === '${value}'`
      : undefined;
    setStatusFilterLoading(true);
    selectedColor.current = value;
    searchPage();
  };

  const setAreaPagePaths = (value) => {
    setTimeFilterValue(value);
    pagePaths.current.time = value
      ? `exists(@${timeFilter}) && @${timeFilter} === '${value}'`
      : undefined;
    setTimeFilterLoading(true);
    searchPage();
  };

  const setTimePagePaths = (type, value) => {
    if (type === "start") {
      startDate.current = value;
    } else {
      endDate.current = value;
    }
    if (startDate.current && endDate.current) {
      pagePaths.current.time = `exists(@createdAt) && @createdAt > ${new Date(
        startDate.current
      ).getTime()} && @createdAt < ${new Date(endDate.current).getTime()}`;
      setTimeFilterLoading(true);
      searchPage();
    } else if (!startDate.current && !startDate.current) {
      pagePaths.current.time = undefined;
      setTimeFilterLoading(true);
      searchPage();
    }
  };

  const handleProjectFilter = (type) => {
    if (projectFilter === type || !projects?.projects || !adminRoles.current)
      return;
    setProjectFilterValue(undefined);
    setProjectFilterOptions([]);
    const options = [];
    switch (type) {
      case "project":
        projects?.projects?.map((project) => {
          options.push({ label: project.name, value: project.id });
        });
        setProjectFilterOptions(options);
        break;
      case "manager":
        getUsersByRole(adminRoles.current, "manager");
        break;
      case "dispatcher":
        getUsersByRole(adminRoles.current, "dispatcher");
        break;
      case "responder":
        getUsersByRole(adminRoles.current, "responder");
        break;
    }
    setProjectFilter(type);
  };

  const handleTimeFilter = (type) => {
    if (timeFilter === type) return;
    setTimeFilterValue(undefined);
    setTimeFilterOptions([]);
    getPageAggregateBds(type);
    setTimeFilter(type);
  };

  const getAdminRoles = (adminUserId, cudbServiceId) => {
    const query = new URLSearchParams(search);
    const connectionId = query.get("connectionId");
    socket.publishToServiceIdSurvey(
      SocketTopics.SurveyTopics,
      UserTopics.SearchInUser,
      getUserRequestPayload(adminUserId, "$", "$..role"),
      getAdminRolesTopic(connectionId),
      cudbServiceId
    );
    socket.subscribeToSurvey(
      SocketTopics.SurveyTopics,
      getAdminRolesTopic(connectionId),
      (response) => {
        adminRoles.current = response.data?.[0]?.[0];
      }
    );
  };

  const getUsersByRole = (roles, type) => {
    const query = new URLSearchParams(search);
    const connectionId = query.get("connectionId");
    let userRoles;
    switch (type) {
      case "dispatcher":
        userRoles = roles.filter(
          (x: any) =>
            x.permissions.indexOf("cad") !== -1 &&
            x.permissions.indexOf("admin") === -1
        );
        break;
      case "responder":
        userRoles = roles.filter(
          (x: any) =>
            x.permissions.indexOf("onMove") !== -1 &&
            x.permissions.indexOf("admin") === -1
        );
        break;
      case "manager":
        userRoles = roles.filter(
          (x: any) =>
            x.permissions.indexOf("cad") !== -1 &&
            x.permissions.indexOf("onMove") !== -1 &&
            x.permissions.indexOf("admin") === -1
        );
        break;
    }
    const rolesQuery = userRoles.map((y: any) => {
      return `@roleId === '${y._id}'`;
    });
    const userRequest: any = {
      select: {
        index: {
          searchIndex: `${process.env.REACT_APP_ECO_SYSTEM}:userNameIndex`,
          searchQuery: `@serviceId:{'${formatForRedis(
            projects?.userServiceId
          )}'}`,
          searchOptions: "TIMEOUT 5000",
        },
        load: {
          id: "$.objectId",
          status: "$.nonpersistent.static.live.status",
          live: "$.persistent.static.live",
          roleId: "$..admin.profile.roleId",
        },
      },
      apply: {
        expression: "",
        name: "",
      },
      result: {},
      filter: {
        expression: `exists(@roleId) && (${rolesQuery.join("||")})`,
      },
      anyStatus: false,
    };
    socket.publishToEvent(
      SocketTopics.PublishPageTopics,
      getAggregateBdsPublishTopic(connectionId),
      wrapRequestPayload(userRequest, `${filterUserRequestId.current}:${type}`)
    );
    setProjectFilterLoading(true);
  };

  const getPageAggregateBds = (aggregateBy) => {
    const query = new URLSearchParams(search);
    const userId = query.get("userId");
    setTimeFilterLoading(true);
    const pageAggregateRequest = {
      select: {
        index: {
          searchIndex: `${process.env.REACT_APP_ECO_SYSTEM}:pageUpdatedAtIndex`,
          searchQuery: `@pageMembers:{'${userId}'}`,
          searchOptions: "VERBATIM TIMEOUT 10000",
        },
        load: {
          [aggregateBy]: `$.persistent.static.profile.${aggregateBy}`,
        },
      },
      groupBy: {
        properties: [`@${aggregateBy}`],
        reducers: [
          {
            func: "COUNT",
            arguments: [],
            name: "count",
          },
        ],
      },
      filter: {
        expression: "",
      },
    };
    dispatch(
      GET_AGGREGATE({
        requestData: pageAggregateRequest,
        callback: (response) => {
          setTimeFilterLoading(false);
          let options = [];
          response?.map((aggregateBy) => {
            if (aggregateBy[Object.keys(aggregateBy)[0]]) {
              options.push({
                label: aggregateBy[Object.keys(aggregateBy)[0]],
                value: aggregateBy[Object.keys(aggregateBy)[0]],
              });
            }
          });
          setTimeFilterOptions(options);
        },
      })
    );
  };

  const getPageById = (pageId) => {
    if (typeof pageId !== "string") return;
    const query = new URLSearchParams(search);
    const userId = query.get("userId");
    const pageAggregateRequest = {
      select: {
        index: {
          searchIndex: `${process.env.REACT_APP_ECO_SYSTEM}:pageUpdatedAtIndex`,
          searchQuery: `@pageMembers:{'${userId}'}`,
          searchOptions: "VERBATIM TIMEOUT 10000",
        },
        list: {
          objectIds: [pageId],
        },
        load: {
          persistent: "$.persistent",
          nonpersistent: "$.nonpersistent",
          pageId: "$.objectId",
        },
      },
      filter: {
        expression: "",
      },
    };
    dispatch(
      GET_AGGREGATE({
        requestData: pageAggregateRequest,
        callback: (response) => {
          setPageUpdated(response?.[0]);
        },
      })
    );
  };

  const getPageStatus = () => {
    const query = new URLSearchParams(search);
    const userId = query.get("userId");
    const expressions = [
      "exists(@profile)",
      "exists(@type)",
      "@type==='event'",
    ];
    const pageStatusCountRequest = {
      select: {
        index: {
          searchIndex: `${process.env.REACT_APP_ECO_SYSTEM}:pageUpdatedAtIndex`,
          searchQuery: `@pageMembers:{'${userId}'}`,
          searchOptions: "VERBATIM TIMEOUT 10000",
        },
        load: {
          profile: "$..profile",
          type: "$..persistent.static.metadata.type",
          persistent: "$.persistent",
          nonpersistent: "$.nonpersistent",
          pageId: "$.objectId",
          iotAssetConfigId:
            "$.persistent.static.iotAsset.config.iotAssetConfigId",
          createdAt: "$.persistent.static.metadata.createdAt",
          status: "$.nonpersistent.static.live.status.name",
        },
      },
      groupBy: {
        properties: ["@status"],
        reducers: [
          {
            func: "COUNT",
            arguments: [],
            name: "count",
          },
        ],
      },
      filter: {
        expression: expressions.join("&&"),
      },
    };
    dispatch(
      GET_AGGREGATE({
        requestData: pageStatusCountRequest,
        callback: (response) => {
          let all = 0;
          let status: any = {
            ...statusTypes.reduce((a, v) => ({ ...a, [v.name]: 0 }), {}),
          };
          response.map((x) => {
            status[x.status] = x.count;
            all += x.count;
          });
          let chartDataTemp = { ...chartData };
          chartDataTemp.datasets[0].data = [
            status.purple,
            status.blue,
            status.amber,
            status.red,
            status.green,
            status.grey,
            status.black,
          ];
          let barDataTemp = { ...barChartData };
          barDataTemp.datasets[0].data[0] = status.purple;
          barDataTemp.datasets[1].data[0] = status.blue;
          barDataTemp.datasets[2].data[0] = status.amber;
          barDataTemp.datasets[3].data[0] = status.red;
          barDataTemp.datasets[4].data[0] = status.green;
          barDataTemp.datasets[5].data[0] = status.grey;
          barDataTemp.datasets[6].data[0] = status.black;
          setChartData(chartDataTemp);
          setBarChartData(barDataTemp);
          setStatusCount({
            all,
            live: all,
            ...status,
          });
        },
      })
    );
  };

  useEffect(() => {
    if (statusCount) {
      setRedrawCount(redrawCount + 1);
    }
  }, [JSON.stringify(statusCount)]);

  useEffect(() => {
    if (pageUpdated) {
      const profile = pageUpdated?.persistent?.static?.profile || {};
      const status = pageUpdated?.nonpersistent?.static?.live?.status?.name;
      let tempPages = [...tableDataSource];
      let index = tempPages.findIndex((page) => {
        return page.key === pageUpdated.pageId;
      });
      getPageStatus();
      if (selectedColor.current && selectedColor.current !== status) {
        if (index !== -1) {
          tempPages.splice(index, 1);
          setTableDataSource(tempPages);
        }
        return;
      }
      const source: any = {};
      Object.keys(profile).map((p) => {
        if (typeof profile[p] === "string") {
          source[p] = profile[p];
        }
      });
      const sourceUpdatedData = {
        key: pageUpdated.pageId,
        pageStatus: pageUpdated?.nonpersistent?.static?.live?.status?.name,
        name: pageUpdated?.persistent?.static?.metadata?.title,
        action: {
          pageId: pageUpdated.pageId,
          page: pageUpdated,
          summary: source.summary,
          cudbServiceId: projects?.userServiceId,
        },
        ...source,
      };
      if (index !== -1) {
        tempPages[index] = sourceUpdatedData;
      } else tempPages.unshift(sourceUpdatedData);
      setPageLocations(
        tempPages?.map((x) => {
          return {
            lat: x?.action?.page?.persistent?.static?.live?.location
              ?.coordinates?.lat,
            lng: x?.action?.page?.persistent?.static?.live?.location
              ?.coordinates.long,
            title: x?.action?.page?.persistent?.static?.metadata?.title,
          };
        })
      );
      setTableDataSource(tempPages);
    }
  }, [pageUpdated]);

  const searchPage = (offset = 0) => {
    const query = new URLSearchParams(search);
    const userId = query.get("userId");
    const expressions = [
      "exists(@profile)",
      "exists(@type)",
      "@type==='event'",
    ];
    if (pagePaths.current) {
      Object.keys(pagePaths.current).map((key) => {
        if (pagePaths.current?.[key]) {
          expressions.push(pagePaths.current?.[key]);
        }
      });
    }
    const load = {};
    if (loadPaths.current) {
      loadPaths.current.map((x) => {
        load[x.key] = x.value;
      });
    }
    pageOffset.current = offset;
    const getPagesRequest = {
      select: {
        index: {
          searchIndex: `${process.env.REACT_APP_ECO_SYSTEM}:pageUpdatedAtIndex`,
          searchQuery: `@pageMembers:{'${userId}'}`,
          searchOptions: "VERBATIM TIMEOUT 10000",
        },
        load: {
          profile: "$..profile",
          type: "$..persistent.static.metadata.type",
          persistent: "$.persistent",
          nonpersistent: "$.nonpersistent",
          pageId: "$.objectId",
          iotAssetConfigId:
            "$.persistent.static.iotAsset.config.iotAssetConfigId",
          createdAt: "$.persistent.static.metadata.createdAt",
          updatedAt:
            "$.nonpersistent.dynamic.metadata.lastSent.members.updatedAt",
          status: "$.nonpersistent.static.live.status.name",
          [timeFilter]: `$..profile.${timeFilter}`,
          ...load,
        },
      },
      filter: {
        expression: expressions.join("&&"),
      },
      sortBy: {
        properties: [
          {
            field: "@updatedAt",
            order: "DESC",
          },
        ],
        max: 10,
      },
      result: {
        pagination: {
          offset: offset,
          limit: 30,
        },
      },
    };
    dispatch(
      GET_AGGREGATE({
        requestData: getPagesRequest,
        callback: (response) => {
          setPages(response);
          pageOffset.current = pageOffset.current + 30;
          let sourceData = offset > 0 ? [...tableDataSource] : [];
          let columns: any = [
            {
              title: "Status",
              key: "pageStatus",
              dataIndex: "pageStatus",
              render: (status: string) => (
                <Button className="beam-btn icon-btn ml-2">
                  <ReactSVG
                    src="/images/flag-icon.svg"
                    className={`${status ?? "gray"}-icon`}
                  />
                </Button>
              ),
            },
            {
              title: "Name",
              key: "name",
              dataIndex: "name",
            },
          ];
          response?.map((x, i) => {
            const profile = x?.persistent?.static?.profile;
            const source: any = {};
            Object.keys(profile).map((p) => {
              if (typeof profile[p] === "string") {
                source[p] = profile[p];
              }
            });
            sourceData.push({
              key: x.pageId,
              pageStatus: x?.nonpersistent?.static?.live?.status?.name,
              name: x?.persistent?.static?.metadata?.title,
              action: {
                pageId: x.pageId,
                page: x,
                summary: source.summary,
                cudbServiceId: projects?.userServiceId,
              },
              ...source,
            });
          });
          sourceData.map((x) => {
            const profile = x?.action?.page?.persistent?.static?.profile;
            Object.keys(profile).map((p) => {
              if (
                columns.findIndex((c) => {
                  return c.key === p;
                }) === -1 &&
                typeof profile[p] === "string"
              ) {
                columns.push({
                  title: attributeKeyGenerator(p),
                  dataIndex: p,
                  key: p,
                });
              }
            });
          });
          columns.push({
            title: "Action",
            key: "action",
            dataIndex: "action",
            render: (action: any) => (
              <Row style={{ width: 90 }}>
                <Col span={12}>
                  <Button
                    className="beam-btn icon-btn"
                    onClick={() => {
                      const query = new URLSearchParams(search);
                      const userId = query.get("userId");
                      socket.publishToEvent(
                        SocketTopics.PublishPageTopics,
                        getFocusPagePublishTopic(action?.pageId, userId),
                        {
                          page: action?.page,
                        }
                      );
                    }}
                  >
                    <EyeOutlined />
                  </Button>
                </Col>

                <Col span={12}>
                  {action?.summary && (
                    <Button
                      className="beam-btn icon-btn ml-2"
                      onClick={() => {
                        const query = new URLSearchParams(search);
                        const connectionId = query.get("connectionId");
                        window.open(
                          `${window.location.origin}/${
                            action?.summary ?? "summary"
                          }?connectionId=${connectionId}&pageId=${
                            action?.pageId
                          }&cudbServiceId=${action?.cudbServiceId}`,
                          "_blank"
                        );
                      }}
                    >
                      <FileSearchOutlined />
                    </Button>
                  )}
                </Col>
              </Row>
            ),
          });
          setTableColumns(columns);
          setTableDataSource(sourceData);
          setPageLocations(
            response
              ?.map((x) => {
                return {
                  lat: x?.persistent?.static?.live?.location?.coordinates?.lat,
                  lng: x?.persistent?.static?.live?.location?.coordinates.long,
                  title: x?.persistent?.static?.metadata?.title,
                };
              })
              ?.filter((x) => {
                return x.lat && x.lng;
              })
          );
          setStatusFilterLoading(false);
          setProjectFilterLoading(false);
          setTimeFilterLoading(false);
        },
      })
    );
    getPageStatus();
  };

  useEffect(() => {
    if (socket.client) {
      const query = new URLSearchParams(search);
      const userId = query.get("userId");
      const connectionId = query.get("connectionId");
      socket.subscribeToEvent(
        SocketTopics.SubscribePageTopics,
        getProjectsSubscriptionTopic(connectionId)
      );
      socket.publishToEvent(
        SocketTopics.PublishPageTopics,
        getProjectsRequestPublishTopic(connectionId),
        wrapRequestPayload({
          userId,
        })
      );
      socket.subscribeToEvent(
        SocketTopics.SubscribePageTopics,
        getMemberPageUpdatedPublishTopic(userId),
        (response) => {
          getPageById(response?.data);
        }
      );
      setProjectFilterLoading(true);
      searchPage();
      getPageAggregateBds("state");
      setTableHideList(JSON.parse(localStorage.getItem("tableHideList")) || []);
    }
  }, [search, socket.client]);

  useEffect(() => {
    if (projects) {
      const query = new URLSearchParams(search);
      const connectionId = query.get("connectionId");
      const options = [];
      projects?.projects?.map((project) => {
        options.push({ label: project.name, value: project.id });
      });
      setProjectFilterOptions(options);
      if (projects?.adminUserId) {
        getAdminRoles(projects?.adminUserId, projects?.userServiceId);
        socket.subscribeToEvent(
          SocketTopics.SubscribePageTopics,
          getUsersSubscriptionTopic(connectionId, projects?.userServiceId)
        );
        socket.subscribeToEvent(
          SocketTopics.SubscribePageTopics,
          getAggregateBdsSubscriptionTopic(connectionId)
        );
      }
    }
  }, [projects]);

  useEffect(() => {
    if (userRequests && socket.client) {
      if (userRequests?.[`${filterUserRequestId.current}:${projectFilter}`]) {
        setProjectFilterLoading(false);
        let options = [];
        userRequests?.[`${filterUserRequestId.current}:${projectFilter}`]?.map(
          (user) => {
            options.push({
              label: `${user.fullName}: ${user.email}`,
              value: user.id,
            });
          }
        );
        setProjectFilterOptions(options);
      }
    }
  }, [userRequests, socket.client]);


  const PageMap = useMemo(() => {
    return (
      <MapPreview
        locations={pageLocations}
        location={{ lat: 31.5272253, lng: 74.4155498 }}
      />
    );
  }, [JSON.stringify(pageLocations)]);

  return (
    <React.Fragment>
      <Row gutter={30}>
        <Col className="gutter-row" sm={24} md={10}>
          <Card className="filter-card">
            <Row gutter={16} className="d-flex px-1 flex-wrap">
              <div>
                <h5 className="mb-0">Filters</h5>
              </div>
              <div
                onClick={() => {
                  handleProjectFilter("manager");
                }}
              >
                <Button
                  className={`beam-btn w-70 ${
                    projectFilter === "manager" ? "green-btn cover" : ""
                  }`}
                >
                  Manager
                </Button>
              </div>
              <div
                onClick={() => {
                  handleProjectFilter("project");
                }}
              >
                <Button
                  className={`beam-btn w-70 ${
                    projectFilter === "project" ? "green-btn cover" : ""
                  }`}
                >
                  Project
                </Button>
              </div>
              <div
                onClick={() => {
                  handleProjectFilter("dispatcher");
                }}
              >
                <Button
                  className={`beam-btn w-70 ${
                    projectFilter === "dispatcher" ? "green-btn cover" : ""
                  }`}
                >
                  Dispatcher
                </Button>
              </div>
              <div
                onClick={() => {
                  handleProjectFilter("responder");
                }}
              >
                <Button
                  className={`beam-btn w-70 ${
                    projectFilter === "responder" ? "green-btn cover" : ""
                  }`}
                >
                  Responder
                </Button>
              </div>
            </Row>
            <Row className="mt-3">
              <Col span={24}>
                <Select
                  className="w-full"
                  placeholder="Select ID"
                  loading={projectFilterLoading}
                  value={projectFilterValue}
                  allowClear
                  options={projectFilterOptions}
                  onChange={(value) => {
                    setProjectPagePaths(value);
                  }}
                ></Select>
              </Col>
            </Row>
            <Row gutter={15} className="d-flex px-2 flex-wrap mt-4">
              <Col sm={24} md={4}>
                <h5 className="mb-0">Status</h5>
              </Col>
            </Row>
            <Row className="mt-3">
              <Col span={24}>
                <Select
                  className="w-full"
                  placeholder="Select ID"
                  loading={statusFilterLoading}
                  allowClear
                  onChange={(value) => {
                    setStatusPagePaths(value);
                  }}
                >
                  {statusTypes.map((item, idx: number) => (
                    <Option value={item.name}>{item.label}</Option>
                  ))}
                </Select>
              </Col>
            </Row>
          </Card>
        </Col>
        <Col className="gutter-row" sm={24} md={10}>
          <Card className="filter-card">
            <Row className="d-flex flex-wrap" id="filters">
              <div
              >
                <h5 className="mb-0">Filters</h5>
              </div>
              <div
                onClick={() => {
                  handleTimeFilter("state");
                }}
              >
                <Button
                  className={`beam-btn w-70 ${
                    timeFilter === "state" ? "green-btn cover" : ""
                  }`}
                >
                  State
                </Button>
              </div>
              <div
                onClick={() => {
                  handleTimeFilter("city");
                }}
              >
                <Button
                  className={`beam-btn w-70 ${
                    timeFilter === "city" ? "green-btn cover" : ""
                  }`}
                >
                  City
                </Button>
              </div>
              <div
                onClick={() => {
                  handleTimeFilter("county");
                }}
              >
                <Button
                  className={`beam-btn w-70 ${
                    timeFilter === "county" ? "green-btn cover" : ""
                  }`}
                >
                  County
                </Button>
              </div>
              <div
                onClick={() => {
                  handleTimeFilter("zip");
                }}
              >
                <Button
                  className={`beam-btn w-70 ${
                    timeFilter === "zip" ? "green-btn cover" : ""
                  }`}
                >
                  Zip
                </Button>
              </div>
            </Row>
            <Row className="mt-3">
              <Col xs={24}>
                <Select
                  className="w-full"
                  placeholder="Select ID"
                  loading={timeFilterLoading}
                  allowClear
                  options={timeFilterOptions}
                  value={timeFilterValue}
                  onChange={(value) => {
                    setAreaPagePaths(value);
                  }}
                ></Select>
              </Col>
            </Row>
            <Row gutter={15} className="d-flex flex-wrap mt-3 mx-0">
              <Col xs={24} md={10} className="datetime-item">
                <img src="/images/time.png" alt="" />
                <DatePicker
                  placeholder="From"
                  suffixIcon={<DownOutlined />}
                  onChange={(time: any) => {
                    setTimePagePaths("start", time);
                  }}
                />
              </Col>
              <Col xs={24} md={4} className="d-flex justify-content-center">
                <ReactSVG src="/images/next.svg" />
              </Col>
              <Col xs={24} md={10} className="datetime-item">
                <img src="/images/time.png" alt="" />
                <DatePicker
                  placeholder="To"
                  suffixIcon={<DownOutlined />}
                  onChange={(time: any) => {
                    setTimePagePaths("end", time);
                  }}
                />
              </Col>
            </Row>
            <Row className="mt-3">
              <Col span={24}>
                <Select
                  className="w-full"
                  placeholder="Select Hour(s)"
                  disabled
                >
                </Select>
              </Col>
            </Row>
          </Card>
        </Col>
        <Col className="gutter-row" sm={24} md={4}>
          <Card className="filter-card">
            <p className="mb-2 text-center">Export to</p>

            <div className="d-flex flex-row flex-md-column justify-content-center">
              <div>
                <Button
                  className="beam-btn export mb-0 mb-md-2 mr-3 mr-md-0"
                  disabled
                >
                  <ReactSVG src="/images/export-csv.svg" />
                </Button>
              </div>
              <div>
                <Button
                  className="beam-btn export mb-0 mb-md-2 mr-3 mr-md-0"
                  disabled
                >
                  <ReactSVG src="/images/export-xls.svg" />
                </Button>
              </div>
              <div>
                <Button
                  className="beam-btn export mb-0 mb-md-2 mr-3 mr-md-0"
                  disabled
                >
                  <ReactSVG src="/images/export-pdf.svg" />
                </Button>
              </div>
            </div>
          </Card>
        </Col>
      </Row>

      {pages && (
        <React.Fragment>
          <Row className="map-chart mt-4">
            <Col xs={24} sm={12} md={8}>
              <div className="main-column message-panel mr-4">
                <div className="main-content p-0">
                  <div className="main-header pt-3 pr-5 pl-4 border-bottom-0">
                    <div className="d-flex">
                    </div>
                    <div className="d-flex">
                      <ReactSVG
                        src="/images/expand-icon.svg"
                        className="close-icon mr-4"
                      />
                      <ReactSVG
                        src="/images/close-icon.svg"
                        className="close-icon"
                      />
                    </div>
                  </div>
                  {PageMap}
                </div>
              </div>
            </Col>
            <Col xs={24} sm={12} md={8}>
              <div className="main-column message-panel mr-4">
                <div className="main-content">
                  <div className="main-header">
                    <div className="d-flex">
                    </div>
                    <div className="d-flex">
                      <ReactSVG
                        src="/images/expand-icon.svg"
                        className="close-icon mr-4"
                      />
                      <ReactSVG
                        src="/images/close-icon.svg"
                        className="close-icon"
                      />
                    </div>
                  </div>
                  <div className="d-flex justify-content-center align-self-center">
                    <Pie key={redrawCount} data={chartData} redraw />
                  </div>
                </div>
              </div>
            </Col>
            <Col xs={24} sm={12} md={8}>
              <div className="main-column message-panel">
                <div className="main-content">
                  <div className="main-header">
                    <div className="d-flex">
                    </div>
                    <div className="d-flex">
                      <ReactSVG
                        src="/images/expand-icon.svg"
                        className="close-icon mr-4"
                      />
                      <ReactSVG
                        src="/images/close-icon.svg"
                        className="close-icon"
                      />
                    </div>
                  </div>
                  <VerticalGraph data={barChartData} />
                </div>
              </div>
            </Col>
          </Row>

          <Row className="d-flex mt-3">
            <Col
              span={2}
              className="border rounded text-center p-2 mx-2 cursor-pointer"
              onClick={() => {
                setStatusPagePaths(undefined);
              }}
            >
              <div className="">
                <span>All</span>
                <h4>{statusCount.live}</h4>
              </div>
            </Col>

            {statusTypes.map((item, idx: number) => (
              <Col
                span={2}
                className="border rounded text-center p-2 mx-2 cursor-pointer"
                onClick={() => {
                  setStatusPagePaths(item.name);
                }}
              >
                <div className={item.name}>
                  <span>{item.label}</span>
                  <h4>{statusCount[item.name]}</h4>
                </div>
              </Col>
            ))}
          </Row>
        </React.Fragment>
      )}

      <Row gutter={20} className="mt-4 scrollable-table">
        <Col
          xs={24}
          sm={24}
          md={24}
          style={{ maxHeight: 1000, overflow: "scroll" }}
        >
          <Button
            className="beam-btn icon-btn"
            style={{
              float: "right",
              margin: "10px 0 10px 0",
            }}
            onClick={() => {
              setShowTableFilterModal(true);
            }}
          >
            <FilterOutlined />
          </Button>

          <Button
            className="beam-btn icon-btn"
            style={{
              float: "right",
              margin: "10px 10px 10px 0",
            }}
            loading={projectFilterLoading || statusFilterLoading}
            onClick={() => {
              setProjectFilterLoading(true);
              searchPage();
            }}
          >
            {!projectFilterLoading && !statusFilterLoading && <RedoOutlined />}
          </Button>

          <Table
            dataSource={tableDataSource}
            onRow={(record) => {
              return {
                onDoubleClick: () => {
                  const query = new URLSearchParams(search);
                  const userId = query.get("userId");
                  socket.publishToEvent(
                    SocketTopics.PublishPageTopics,
                    getFocusPagePublishTopic(record?.action?.pageId, userId),
                    {
                      page: record?.action?.page,
                    }
                  );
                },
              };
            }}
            columns={tableColumns?.filter((x) => {
              return tableHideList?.indexOf(x.key) === -1;
            })}
            scroll={{ x: true }}
          />
          <Row>
            <Col
              span={24}
              className="d-flex justify-content-center align-items-center"
            >
              <Button
                className="beam-btn mt-3 mb-3"
                loading={projectFilterLoading}
                onClick={() => {
                  setProjectFilterLoading(true);
                  searchPage(pageOffset.current);
                }}
              >
                Load more
              </Button>
            </Col>
          </Row>
          {showTableFilterModal && (
            <TableFilterModal
              socket={socket}
              tableHideList={tableHideList}
              onChange={(e) => {
                setTableHideList(e);
                localStorage.setItem("tableHideList", JSON.stringify(e));
                setShowTableFilterModal(false);
              }}
              columns={tableColumns}
              onClose={() => {
                setShowTableFilterModal(false);
              }}
            />
          )}
        </Col>
      </Row>
    </React.Fragment>
  );
};

export default DashboardModal;
